import React, {useEffect, useState} from 'react';
import ImageListItem from "@mui/material/ImageListItem";
import Skeleton from '@mui/material/Skeleton';
import ImageList from "@mui/material/ImageList";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from '@mui/material/styles';
import ImageListItemBar from "@mui/material/ImageListItemBar";
import IconButton from "@mui/material/IconButton";
import moment from "moment";
import {Grid} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import StarIcon from '@mui/icons-material/Star';
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import {connect} from 'react-redux'
import {fetchGallery, productSetMainImage, uploadFiles} from "../../../../../store/actions/store/ProductAction";
import DeleteDialog from "./DeleteDialog";
import {useTranslation} from 'react-i18next';
import Empty from "./Empty";
import {PRODUCT_GALLERY_IMAGE_VARIATION} from "../../../../../store/reducers/store/ProductReducer";
import DeleteIcon from '@mui/icons-material/Delete';
import Toolbar from "../../../typography/Toolbar";
import Button from "../../../inputs/Button";


// Using inline styles for better compatibility with MUI v5
const useStyles = () => ({
    icon: {
        color: 'rgba(255, 255, 255, 0.54)',
        '&:hover': {
            color: 'rgba(255, 255, 255, 0.8)',
        }
    },
    iconMain: {
        color: 'rgba(255, 255, 255, 1)',
        '&:hover': {
            color: 'rgba(255, 255, 255, 0.8)',
        }
    },
    titleBar: {
        background:
            'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, ' +
            'rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
    },
});

const Gallery = ({
                     productId,
                     uploadFiles,
                     uploadImgCount,
                     uploadImgDoneCount,
                     uploadImgRunningCount,
                     uploadImgRunningPercent,
                     fetchGallery,
                     loading,
                     gallery,
                     isManager,
                     setMainImage,
                     settingMainImage
                 }) => {

    useEffect(() => {
        if (!productId) return;
        fetchGallery(productId);
    }, [productId, fetchGallery]);

    const inputFileRef = React.useRef(null);
    const {t} = useTranslation();
    const classes = useStyles();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

    const [deleteDialogShown, setDeleteDialogShown] = useState(false);
    const [selectedImg, setSelectedImg] = useState(null);

    const loadingData = [...Array(5).keys()];
    const displayLoading = () => loadingData.map((k) =>
        <ImageListItem key={k}>
            <Skeleton width={200} height={180}/>
        </ImageListItem>);

    const displayImages = () => gallery.map((g) => {
            const img = g.Drive_Item;
            return (
                <ImageListItem key={img.Blob__}>
                    <img src={img.Media_Image[PRODUCT_GALLERY_IMAGE_VARIATION]} alt={img.Filename}/>
                    {isManager && <ImageListItemBar
                        titlePosition="top"
                        actionIcon={
                            <IconButton
                                disabled={settingMainImage}
                                className={g.Role === 'main' ? classes.iconMain : classes.icon}
                                onClick={() => setMainImage(g.Catalog_Product_Image__)}
                                size="large">
                                <StarIcon/>
                            </IconButton>
                        }
                        className={classes.titleBar}
                        actionPosition="left"
                    />}
                    {isManager && <ImageListItemBar
                        title={img.Filename}
                        subtitle={moment(parseInt(img.Created.unixms)).format('LLL')}

                        actionIcon={
                            <IconButton
                                disabled={settingMainImage}
                                className={classes.icon}
                                onClick={() => {
                                    setSelectedImg(g);
                                    setDeleteDialogShown(true)
                                }}
                                size="large">
                                <DeleteIcon/>
                            </IconButton>
                        }
                    />
                    }
                </ImageListItem>
            );
        }
    );

    const displayUpload = uploadImgCount !== uploadImgDoneCount;

    let donePercent = 0;
    let bufferPercent = 0;

    if (displayUpload) {
        donePercent = (100 * uploadImgDoneCount) / uploadImgCount;
        bufferPercent = (100 * (uploadImgDoneCount + uploadImgRunningCount)) / uploadImgCount;
        // calculate the % of the currently uploading
        donePercent += ((bufferPercent - donePercent) * uploadImgRunningPercent)
    }

    const handleFileInputChange = (e) => {
        if (e.target.files.length < 1) {
            return;
        }

        uploadFiles(productId, e.target.files)
    };

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Toolbar title={t('gallery_title')}>
                    {(!loading && isManager) &&
                    <input accept="image/png, image/jpeg, image/jpg, image/svg, image/gif" multiple={true} type='file'
                           style={{display: 'none'}} ref={inputFileRef}
                           onChange={handleFileInputChange}/>}
                    {(!loading && isManager) &&
                    <Button variant='contained' color='primary' disabled={displayUpload || settingMainImage}
                            endIcon={<AddIcon/>}
                            loading={settingMainImage}
                            className={classes.addBtn}
                            onClick={() => {
                                inputFileRef.current.click()
                            }}
                    >
                        {t('add_btn')}
                    </Button>}
                </Toolbar>
            </Grid>
            <Grid item xs={12}>
                <Typography variant="body1" color="textSecondary" component="p">
                    {t('product_gallery_desc')}
                </Typography>
            </Grid>
            <Grid item xs={12}>
                {displayUpload && <LinearProgress variant="buffer" value={donePercent} valueBuffer={bufferPercent}/>}
            </Grid>
            <Grid item xs={12}>
                <ImageList cellHeight={180} cols={fullScreen ? 3 : 5}>
                    {loading && displayLoading()}
                    {!loading && displayImages()}
                    {(selectedImg) &&
                    <DeleteDialog productId={productId} open={deleteDialogShown} setOpen={setDeleteDialogShown}
                                  img={selectedImg}/>}
                </ImageList>

                {!loading && gallery.length < 1 && <Empty/>}
            </Grid>
        </Grid>
    );
};

const mapStateToProps = (state) => {
    return {
        uploadImgCount: state.product.uploadImgCount,
        uploadImgDoneCount: state.product.uploadImgDoneCount,
        uploadImgRunningCount: state.product.uploadImgRunningCount,
        uploadImgRunningPercent: state.product.uploadImgRunningPercent,
        loading: state.product.galleryLoading,
        gallery: state.product.gallery,
        settingMainImage: state.product.setMainImage,
        isManager: state.access.manager
    }
};

const mapDispatchToProps = (dispatch) => {
    return {
        uploadFiles: (productId, files) => dispatch(uploadFiles(productId, files, {
            role: 'list',
            image_variation: PRODUCT_GALLERY_IMAGE_VARIATION
        })),
        fetchGallery: (productId) => dispatch(fetchGallery(productId)),
        setMainImage: (imgId) => dispatch(productSetMainImage(imgId)),
    }
};
export default connect(mapStateToProps, mapDispatchToProps)(Gallery);
